import { Bot, Building2, Cpu, Search, User } from "lucide-react";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/organisms/dialog";
import { cn } from "@/lib/utils";
import { AccountSettings } from "@/routes/settings/components/account-settings";
import { AgentSettings } from "@/routes/settings/components/agent/agent-settings";
import { AiSettings } from "@/routes/settings/components/ai-providers/ai-settings";
import { SearchSettings } from "@/routes/settings/components/search-engines/search-settings";
import { WorkspaceSettings } from "@/routes/settings/components/workspace-settings";

type Tab = "ai" | "agent" | "search" | "workspaces" | "account";

const tabs: { id: Tab; icon: typeof Cpu; label: string }[] = [
  { id: "ai", icon: Cpu, label: "AI Models" },
  { id: "agent", icon: Bot, label: "Agent" },
  { id: "search", icon: Search, label: "Search" },
  { id: "workspaces", icon: Building2, label: "Workspaces" },
  { id: "account", icon: User, label: "Account" },
];

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const [activeTab, setActiveTab] = useState<Tab>("ai");

  useEffect(() => {
    function handleOpenWorkspaces() {
      setActiveTab("workspaces");
      onOpenChange(true);
    }
    window.addEventListener("open-settings-workspaces", handleOpenWorkspaces);
    return () => window.removeEventListener("open-settings-workspaces", handleOpenWorkspaces);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-3xl h-[80dvh] p-0 gap-0 flex flex-col overflow-hidden"
      >
        <DialogTitle className="sr-only">Settings</DialogTitle>
        <div className="flex flex-1 min-h-0">
          <nav className="w-44 shrink-0 border-r py-3 flex flex-col gap-0.5 px-2 overflow-y-auto">
            {tabs.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors text-left",
                  activeTab === id
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <Icon className="size-4 shrink-0" />
                {label}
              </button>
            ))}
          </nav>
          <main className="flex-1 overflow-y-auto p-6">
            {activeTab === "ai" && <AiSettings />}
            {activeTab === "agent" && <AgentSettings />}
            {activeTab === "search" && <SearchSettings />}
            {activeTab === "workspaces" && <WorkspaceSettings />}
            {activeTab === "account" && <AccountSettings />}
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
